import { graphql } from 'react-relay';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { useQueryNoFurtherUpdateInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryNoFurtherUpdateInterop.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';

import type {
	useSpaceDetailQuery$data,
	useSpaceDetailQuery$variables,
} from './__generated__/useSpaceDetailQuery.graphql.ts';

export type SpaceDetailDataType = {
	spaceId: string;
	type: string;
	name: string;
	alias?: string;
	dataClassificationTags: ReadonlyArray<string | null | undefined>;
	homepage?: {
		id: string;
		title: string;
		type: string;
	};
	iconPath?: string;
};

interface CommonHookResult {
	loading: boolean;
	error?: ApolloErrorInterop;
}

export interface SpaceDetailType extends CommonHookResult {
	data?: SpaceDetailDataType;
}

export const useSpaceDetail = (spaceKey: string | null | undefined): SpaceDetailType => {
	/** @schema cc */
	const useSpaceDetailQuery = graphql`
		query useSpaceDetailQuery($spaceKey: String) {
			space(key: $spaceKey) {
				id
				name
				alias
				key
				type
				dataClassificationTags
				homepage {
					id
					title
					type
				}
				icon {
					path
				}
			}
		}
	`;
	const options = {
		variables: { spaceKey },
	};

	const { loading, data, error } = fg('stop_watching_more_queries')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryNoFurtherUpdateInterop<useSpaceDetailQuery$data, useSpaceDetailQuery$variables>(
				useSpaceDetailQuery,
				options,
			)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useQueryInterop<useSpaceDetailQuery$data, useSpaceDetailQuery$variables>(
				useSpaceDetailQuery,
				options,
			);

	return {
		loading,
		data: data
			? ({
					spaceId: data.space?.id,
					type: data.space?.type,
					name: data.space?.name,
					dataClassificationTags: data.space?.dataClassificationTags,
					alias: data.space?.alias,
					homepage: data.space?.homepage,
					iconPath: data.space?.icon?.path,
				} as any)
			: undefined,
		error,
	};
};
